import {
  Alert,
  Box,
  Button,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  createEvent,
  fetchEventDetail,
  fetchEvents,
  selectEventById,
  selectDetailState,
  updateEvent,
} from "../store/slices/eventsSlice";
import {
  formatDateInputValue,
  formatJakartaEndOfDay,
  formatJakartaStartOfDay,
} from "../utils/date";
import { validateEventForm } from "../utils/validation";

const defaultValues = {
  name: "",
  start_datetime: "",
  end_datetime: "",
  status: "ACTIVE",
  event_rule: "GBK_LOCKED",
  limit_per_ticket: 0,
};

const EventFormPage = ({ mode = "create" }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { eventId } = useParams();
  const isEdit = mode === "edit" && eventId;
  const eventData = useSelector((state) =>
    selectEventById(state, Number(eventId))
  );
  const detailState = useSelector(selectDetailState);
  const [values, setValues] = useState(() => ({ ...defaultValues }));
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const startInputRef = useRef(null);
  const endInputRef = useRef(null);

  useEffect(() => {
    if (isEdit && eventId && !eventData) {
      dispatch(fetchEventDetail(Number(eventId)));
    }
  }, [dispatch, isEdit, eventId, eventData]);

  useEffect(() => {
    if (isEdit) {
      if (eventData) {
        setValues({
          name: eventData.name,
          start_datetime: eventData.start_datetime,
          end_datetime: eventData.end_datetime,
          status: eventData.status,
          event_rule: eventData.event_rule,
          limit_per_ticket: eventData.limit_per_ticket,
        });
      }
    } else {
      setValues({ ...defaultValues });
    }
  }, [isEdit, eventData]);

  const title = isEdit ? "Update Event" : "Create Event";
  const subtitle = isEdit
    ? "Update scheduling, quota, or gating rules for the selected event."
    : "Configure a new event for the Special Lane program.";

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleDateChange = (field) => (event) => {
    const rawValue = event.target.value;
    const isoValue =
      field === "end_datetime"
        ? formatJakartaEndOfDay(rawValue)
        : formatJakartaStartOfDay(rawValue);
    setValues((prev) => ({ ...prev, [field]: isoValue }));
  };

  const handleOpenDatePicker = (ref) => () => {
    const input = ref.current;
    if (!input) return;
    const wasReadOnly = input.readOnly;
    input.readOnly = false;
    if (input.showPicker) {
      try {
        input.showPicker();
      } catch (error) {
        input.focus();
      }
    } else {
      input.focus();
    }
    input.readOnly = wasReadOnly;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError(null);
    const validationErrors = validateEventForm({
      ...values,
      limit_per_ticket: values.limit_per_ticket === "" ? "" : Number(values.limit_per_ticket),
    });
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    const payload = {
      ...values,
      limit_per_ticket:
        values.limit_per_ticket === "" ? 0 : Number(values.limit_per_ticket),
    };

    try {
      if (isEdit && eventId) {
        await dispatch(
          updateEvent({
            id: Number(eventId),
            payload,
          })
        ).unwrap();
      } else {
        await dispatch(createEvent(payload)).unwrap();
      }
      dispatch(fetchEvents());
      navigate("/events");
    } catch (errorResponse) {
      setSubmitError(
        errorResponse || "An unexpected error occurred while saving the event."
      );
      setIsSubmitting(false);
    }
  };

  const statusOptions = useMemo(
    () => [
      { value: "ACTIVE", label: "Active" },
      { value: "INACTIVE", label: "Inactive" },
    ],
    []
  );

  const ruleOptions = useMemo(
    () => [
      { value: "GBK_LOCKED", label: "GBK Locked" },
      { value: "NO_RESTRICTION", label: "No Restriction" },
    ],
    []
  );

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={3}>
        <Typography variant="h4" fontWeight={700}>
          {title}
        </Typography>
        <Typography color="text.secondary">{subtitle}</Typography>
        <Paper sx={{ p: 3 }}>
          {isEdit && !eventData && detailState.loading && (
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Loading event information...
            </Typography>
          )}
          {isEdit && detailState.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {detailState.error}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Event Name"
                value={values.name}
                onChange={handleInputChange("name")}
                error={Boolean(errors.name)}
                helperText={errors.name}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Status"
                value={values.status}
                onChange={handleInputChange("status")}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Event Rule"
                value={values.event_rule}
                onChange={handleInputChange("event_rule")}
              >
                {ruleOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                InputLabelProps={{ shrink: true }}
                value={formatDateInputValue(values.start_datetime)}
                onChange={handleDateChange("start_datetime")}
                error={Boolean(errors.start_datetime)}
                helperText={errors.start_datetime}
                inputRef={startInputRef}
                onClick={handleOpenDatePicker(startInputRef)}
                InputProps={{
                  readOnly: true,
                }}
                inputProps={{ style: { cursor: "pointer" } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                InputLabelProps={{ shrink: true }}
                value={formatDateInputValue(values.end_datetime)}
                onChange={handleDateChange("end_datetime")}
                error={Boolean(errors.end_datetime)}
                helperText={errors.end_datetime}
                inputRef={endInputRef}
                onClick={handleOpenDatePicker(endInputRef)}
                InputProps={{
                  readOnly: true,
                }}
                inputProps={{ style: { cursor: "pointer" } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Limit per Ticket"
                value={values.limit_per_ticket}
                onChange={handleInputChange("limit_per_ticket")}
                helperText={
                  errors.limit_per_ticket || "Set 0 for unlimited quota"
                }
                error={Boolean(errors.limit_per_ticket)}
              />
            </Grid>
          </Grid>
          {submitError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {submitError}
            </Alert>
          )}
          <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 4 }}>
            <Button variant="outlined" onClick={() => navigate("/events")} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : isEdit ? "Update Event" : "Create Event"}
            </Button>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
};

export default EventFormPage;
